"use client";

import { useState, useEffect } from "react";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { 
  HomeIcon, ListBulletIcon, CalendarDaysIcon, BanknotesIcon, 
  EnvelopeOpenIcon, Cog6ToothIcon, ArrowRightOnRectangleIcon,
  Bars3Icon, XMarkIcon, ArrowPathIcon, UserGroupIcon, TrophyIcon, ArchiveBoxIcon, DocumentIcon,ArrowTrendingUpIcon,
  SparklesIcon, ShieldCheckIcon, QuestionMarkCircleIcon, ChartBarIcon, AdjustmentsHorizontalIcon
} from "@heroicons/react/24/outline";
import { Toaster } from "react-hot-toast";
import { useLanguage } from "./context/LanguageContext";
import LanguageDropdown from "./components/LanguageDropdown";
import { api } from "./utils/api";

export default function SidebarLayout({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  const router = useRouter();
  const { language, setLanguage, t } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const [isAuthChecking, setIsAuthChecking] = useState(true);
  const [expiredWishesCount, setExpiredWishesCount] = useState(0);
  const [isAdmin, setIsAdmin] = useState(false);
  const [globalSettings, setGlobalSettings] = useState<any>({});

  const fetchExpiredWishesCount = async () => {
    const token = localStorage.getItem("token");
    if (!token) return;
    try {
      const res = await api.get("/api/wishlist/expired-count");
      if (res.ok) {
        const data = await res.json();
        setExpiredWishesCount(data.count || 0);
      }
    } catch (err) {
      console.error("Error fetching expired wishes count:", err);
    }
  };

  const fetchUserSettings = async () => {
    const token = localStorage.getItem("token");
    if (!token) return;
    try {
      const res = await api.get("/api/settings");
      if (res.ok) {
        const data = await res.json();
        setIsAdmin(!!data.admin);
        const selectedTheme = data.theme || "dark";
        if (selectedTheme === "light") {
          document.documentElement.classList.add("theme-light");
          document.documentElement.setAttribute("data-theme", "light");
        } else {
          document.documentElement.classList.remove("theme-light");
          document.documentElement.removeAttribute("data-theme");
        }
      }
      
      const globalRes = await api.get("/api/settings/global");
      if (globalRes.ok) {
        const globalData = await globalRes.json();
        setGlobalSettings(globalData);
        localStorage.setItem("global_settings", JSON.stringify(globalData));
      }
    } catch (err) {
      console.error("Error fetching user settings:", err);
    }
  };

  useEffect(() => {
    try {
      const cached = localStorage.getItem("global_settings");
      if (cached) {
        setGlobalSettings(JSON.parse(cached));
      }
    } catch (_) {}
  }, []);

  useEffect(() => {
    const isAuthPage = publicPages.includes(pathname);
    if (!isAuthPage) {
      fetchExpiredWishesCount();
      fetchUserSettings();
      const interval = setInterval(fetchExpiredWishesCount, 60000);
      return () => clearInterval(interval);
    }
  }, [pathname]);

  // Elenco delle pagine pubbliche che non richiedono il token
  const publicPages = ['/login', '/register', '/forgot-password', '/reset-password', '/verify-email'];

  useEffect(() => {
    setIsOpen(false);
  }, [pathname]);

  useEffect(() => {
    const isAuthPage = publicPages.includes(pathname);
    const token = localStorage.getItem("token");

    if (!token && !isAuthPage) {
      router.push("/login");
    } else {
      setIsAuthChecking(false);
    }
  }, [pathname, router]);

  // Schermata nera temporanea mentre il check è in corso
  if (isAuthChecking) {
    return <div className="min-h-screen bg-gray-900"></div>;
  }

  // Se siamo in una pagina pubblica, non renderizziamo la barra laterale
  if (publicPages.includes(pathname)) return <>{children}</>;

  const handleLogout = () => {
    localStorage.removeItem("token");
    router.push("/login");
  };

  const systemItems = [
    ...(globalSettings.allow_data_export !== false ? [{ name: t("sidebar.data"), href: "/data", icon: ArchiveBoxIcon }] : []),
    { name: t("sidebar.settings"), href: "/settings", icon: Cog6ToothIcon },
    { name: t("sidebar.support") || "Assistenza", href: "/support", icon: QuestionMarkCircleIcon },
  ];

  const navGroups = [
    {
      title: t("sidebar.main"),
      items: [
        { name: t("sidebar.dashboard"), href: "/dashboard", icon: HomeIcon },
        { name: t("sidebar.transactions"), href: "/transactions", icon: ListBulletIcon },
        ...(globalSettings.investments_enabled !== false ? [{ name: t("sidebar.investments"), href: '/investments', icon: ArrowTrendingUpIcon }] : []),
      ]
    },
    {
      title: t("sidebar.planning"),
      items: [
        { name: t("sidebar.recurring"), href: "/recurring", icon: ArrowPathIcon },
        { name: t("sidebar.envelopes"), href: "/envelopes", icon: EnvelopeOpenIcon },
        { name: t("sidebar.debts"), href: "/debts", icon: UserGroupIcon },
        { name: t("sidebar.goals"), href: "/goals", icon: TrophyIcon },
        { name: t("sidebar.wishlist"), href: "/wishlist", icon: SparklesIcon },
        { name: t("sidebar.documents"), href: "/documents", icon: DocumentIcon },
      ]
    },
    {
      title: t("sidebar.closings"),
      items: [
        { name: t("sidebar.balance"), href: "/balance", icon: BanknotesIcon },
      ]
    },
    {
      title: t("sidebar.system"),
      items: systemItems
    }
  ];

  if (isAdmin) {
    navGroups.push({
      title: t("sidebar.admin_section") || "Amministrazione",
      items: [
        { name: t("sidebar.admin_users") || "Gestione Utenti", href: "/admin/users", icon: UserGroupIcon },
        { name: t("sidebar.admin_market") || "Monitor Titoli", href: "/admin/market", icon: ArrowTrendingUpIcon },
        { name: t("sidebar.admin_tickets") || "Gestione Ticket", href: "/admin/tickets", icon: ShieldCheckIcon },
        { name: t("sidebar.admin_analytics") || "Statistiche Uso", href: "/admin/analytics", icon: ChartBarIcon },
        { name: t("sidebar.admin_settings") || "Impostazioni Sistema", href: "/admin/settings", icon: AdjustmentsHorizontalIcon }
      ]
    });
  }

  // Elementi per la barra in basso su Mobile
  const bottomNavItems = [
    { name: t("sidebar.home"), href: "/dashboard", icon: HomeIcon },
    { name: t("sidebar.expenses"), href: "/transactions", icon: ListBulletIcon },
    { name: t("sidebar.envelopes_short"), href: "/envelopes", icon: EnvelopeOpenIcon },
  ];

  const renderNavLinks = () => (
    <div className="flex flex-col gap-6 mt-6 flex-1 overflow-y-auto scrollbar-hide">
      {navGroups.map((group, index) => (
        <div key={index}>
          <div className="px-4 text-[10px] font-black text-gray-500 uppercase tracking-widest mb-2">
            {group.title}
          </div>
          <div className="flex flex-col gap-1 px-2">
            {group.items.map((item) => {
              const Icon = item.icon;
              const isActive = pathname.startsWith(item.href); 
              
              return (
                <Link
                  key={item.name}
                  href={item.href}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-bold transition-all relative overflow-hidden group
                    ${isActive 
                      ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/30 shadow-sm' 
                      : 'text-gray-400 hover:bg-gray-800 hover:text-white border border-transparent'}`}
                >
                  {isActive && (
                    <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-2/3 bg-emerald-500 rounded-r-full shadow-[0_0_8px_rgba(16,185,129,0.8)]"></div>
                  )}
                  <Icon className={`w-5 h-5 transition-transform duration-200 ${isActive ? 'text-emerald-400 scale-110' : 'text-gray-500 group-hover:scale-110'}`} />
                  <span className="relative z-10 flex-1">{item.name}</span>
                  {item.href === "/wishlist" && expiredWishesCount > 0 && (
                    <span className="relative z-10 bg-red-500 text-white text-[10px] font-black px-2 py-0.5 rounded-full animate-pulse shadow-[0_0_8px_rgba(239,68,68,0.8)]">
                      {expiredWishesCount}
                    </span>
                  )}
                </Link>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div className="flex min-h-screen bg-gray-900 text-white font-sans">
      
      {/* === MENU MOBILE (Overlay "Altro") === */}
      {isOpen && (
        <div className="fixed inset-0 z-50 flex md:hidden animate-in fade-in duration-200">
          <div className="fixed inset-0 bg-black/80 backdrop-blur-sm" onClick={() => setIsOpen(false)}></div>
          
          <div className="relative w-72 max-w-[85vw] bg-gray-900 border-r border-gray-700 h-full flex flex-col p-4 shadow-2xl animate-in slide-in-from-left duration-300">
            <div className="flex items-center justify-between mb-2 px-2">
              <span className="text-2xl font-black text-transparent bg-clip-text bg-gradient-to-r from-amber-200 via-amber-300 to-yellow-500">
                Menu
              </span>
              <button onClick={() => setIsOpen(false)} className="text-gray-400 hover:text-white p-1 rounded-lg hover:bg-gray-800 transition">
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>
            {renderNavLinks()}
<LanguageDropdown />
            <div className="mt-2 pt-2 border-t border-gray-800">
                <button onClick={handleLogout} className="w-full flex items-center justify-center gap-2 text-red-400 hover:text-white hover:bg-red-500/20 transition-colors p-3 rounded-xl border border-transparent hover:border-red-500/30 font-bold">
                <ArrowRightOnRectangleIcon className="w-5 h-5" /> {t("sidebar.logout")}
                </button>
            </div>
          </div>
        </div>
      )}

      {/* === SIDEBAR DESKTOP === */}
      <div className="hidden md:flex md:w-64 md:flex-col md:fixed md:inset-y-0 bg-gray-900 border-r border-gray-700 shadow-xl z-40">
        <div className="flex flex-col flex-1 min-h-0 p-4">
          <div className="flex items-center h-12 mb-2 px-2 mt-2">
            <span className="text-2xl font-black tracking-tight">
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-200 via-amber-300 to-yellow-500">MS</span>
              <span className="text-gray-100 font-semibold">Ledger</span>
            </span>
          </div>
          {renderNavLinks()}
<LanguageDropdown />
            <div className="mt-2 pt-2 border-t border-gray-800">
              <button onClick={handleLogout} className="w-full flex items-center justify-center gap-2 text-red-400 hover:text-white hover:bg-red-500/20 transition-colors p-3 rounded-xl border border-transparent hover:border-red-500/30 font-bold">
                <ArrowRightOnRectangleIcon className="w-5 h-5" /> {t("sidebar.logout")}
              </button>
            </div>
        </div>
      </div>

      {/* === CONTENUTO PRINCIPALE === */}
      <div className="flex-1 flex flex-col min-w-0 md:pl-64">
        
        {/* Header Top Mobile (Solo Logo, niente hamburger) */}
        <div className="md:hidden flex items-center justify-center bg-gray-900 p-3 border-b border-gray-700 sticky top-0 z-30 shadow-sm">
          <span className="text-xl font-black tracking-tight">
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-200 via-amber-300 to-yellow-500">MS</span>
            <span className="text-gray-100 font-semibold">Ledger</span>
          </span>
        </div>

        {/* pb-24 serve per non far nascondere il contenuto dalla barra di navigazione inferiore */}
        <main className="flex-1 pb-24 md:pb-0">
          <Toaster 
            position="bottom-right" 
            toastOptions={{
              style: { background: '#1f2937', color: '#fff', border: '1px solid #374151' },
              success: { iconTheme: { primary: '#10b981', secondary: '#fff' } },
              error: { iconTheme: { primary: '#ef4444', secondary: '#fff' } },
            }} 
          />
          {children}
        </main>
      </div>

      {/* === BOTTOM NAVBAR MOBILE === */}
      {/* Usiamo pb-[env(safe-area-inset-bottom)] per supportare il "baffo" degli iPhone */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 bg-gray-900/95 backdrop-blur-md border-t border-gray-800 flex justify-around items-center h-[68px] z-40 pb-[env(safe-area-inset-bottom)]">
        {bottomNavItems.map((item) => {
          const Icon = item.icon;
          const isActive = pathname.startsWith(item.href);
          return (
            <Link key={item.name} href={item.href} className="flex flex-col items-center justify-center w-full h-full gap-1 pt-1">
              <Icon className={`w-6 h-6 transition-transform ${isActive ? 'text-emerald-400 scale-110' : 'text-gray-400'}`} />
              <span className={`text-[10px] font-bold ${isActive ? 'text-emerald-400' : 'text-gray-500'}`}>{item.name}</span>
            </Link>
          );
        })}
        {/* Tasto Menu che apre l'overlay laterale con le altre voci */}
        <button onClick={() => setIsOpen(true)} className="flex flex-col items-center justify-center w-full h-full gap-1 pt-1">
          <Bars3Icon className="w-6 h-6 text-gray-400" />
          <span className="text-[10px] font-bold text-gray-500">{t("sidebar.more")}</span>
        </button>
      </div>

    </div>
  );
}